import {
  GoAButton,
  GoAButtonGroup,
  GoADropdown,
  GoADropdownItem,
  GoAFormItem,
  GoAIconButton,
  GoAModal,
  GoATextArea,
} from "@abgov/react-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Notification from "../../components/Notification";
import { Order, OrderItem } from "../../model/Order";
import { useRecoilState } from "recoil";
import { orderPdfState, orderState } from "../../states/Order.state";
import { TextField, Typography, useMediaQuery } from "@mui/material";
import { OrderService } from "../../services/OrderService";
import {
  ORDER_STATUS_CANCELLED,
  ORDER_STATUS_COMPLETE,
  ORDER_STATUS_PARTIALLYCOMPLETE,
  orderActionStatusList,
  orderTypeOptions
} from "../../common/constants";
import {
  loadCommonLookups,
  notificationMessageState,
  showNotificationState,
} from "../../states/Common.state";
import { INotificationMessage } from "../../model/Common";
import { OrderPdf } from "../../model/OrderPdf";
import NumericInput from "../../components/NumericInput";
import PageLoader from "../../components/PageLoader";
import OrderComments from "./OrderComments";
import { UserComment } from "../../model/Base";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useAuth } from "react-oidc-context";
import DomainItemAutocomplete from "../../components/DomainItemPicker";
import CrewTypePicker from "../../components/CrewTypePicker";
import { getDataFromSessionStorage } from "../../utils/SessionStorageUtils";
import { ForObjectType } from "../../model/enums/Constants";
import { DomainLookupType } from "../../services/DomainService";

interface IOrderDetailProps {
  orderData: Order;
  redirectedFrom?: string;
}
dayjs.extend(advancedFormat);
const OrderViewUpdateByprocessor: React.FC<IOrderDetailProps> = (props) => {
  // const [loading, setLoading] = useState(false);
  //const isTablet = useMediaQuery('(max-width: 1024px)');
  const isMobile = useMediaQuery("(max-width: 767px)");
  const navigate = useNavigate();
  const [showCancelOrderWarning, setShowCancelOrderWarning] =
    useState<boolean>(false);
  const [showDiscardChangesWarning, setShowDiscardChangesWarning] =
    useState<boolean>(false);
  const [loading, setIsLoading] = useState(false);
  const [orderPdf, setOrderPdf] = useRecoilState<OrderPdf>(orderPdfState);
  const [showNotification, setShowNotification] = useRecoilState<boolean>(showNotificationState);
  const [notificationMessage, setNotificationMessage] = useRecoilState<INotificationMessage>(notificationMessageState);

  const [orderData, setOrderData] = useRecoilState<Order>(orderState);
  const [formStateChanged, setFormStateChanged] = useState<boolean>(false);
  const [orderStatus, setOrderStatus] = useState<number>(orderData.status);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [reasonForCancellation, setReasonForCancellation] = useState<string>("");
  const [orderTypeId, setOrderTypeId] = useState<string>("");
  const auth = useAuth();
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (focusIndex !== null) {
      const input = inputRefs.current[focusIndex];
      if (input) {
        input.focus();
        setFocusIndex(null);
      }
    }
  }, [orderData.orderItems, focusIndex]);

  useEffect(() => {
    const fetchData = async () => {
      let lookupTypes = getDataFromSessionStorage(ForObjectType.DOMAIN_LOOKUP_TYPES);

      if (!lookupTypes) {
        await loadCommonLookups();
        lookupTypes = getDataFromSessionStorage(ForObjectType.DOMAIN_LOOKUP_TYPES);
      }

      const orderTypelookupName = orderTypeOptions.find(opt => opt.value === orderData.orderType)?.name ?? "";

      const orderTypeId = lookupTypes
        ?.find((x: DomainLookupType) => x.name === orderTypelookupName)
        ?.id;
      setOrderTypeId(orderTypeId);
    };

    fetchData();

  },);
  const handleUpdateStatus = async (statusVal: string) => {
    setShowNotification(false);
    if (Number(statusVal) !== Number(orderData.status)) {
      setFormStateChanged(true);
    }
    setOrderStatus(Number(statusVal));
  };

  const handleItemNameChange = (name: string, val: string, index: number) => {
    setErrors({});
    setShowNotification(false);
    const orderItems = [...(orderData?.orderItems ?? [])];
    if (val !== orderData.orderItems[index].itemName) {
      setFormStateChanged(true);
      orderItems[index] = { ...orderItems[index], itemName: val };
      setOrderData({ ...orderData, orderItems });
    }
  };

  const handleQuantityChange = (val: number, index: number) => {
    setErrors({});
    setShowNotification(false);
    const orderItems = [...(orderData?.orderItems ?? [])];
    if (val !== orderData.orderItems[index].quantityOrdered) {
      setFormStateChanged(true);
      orderItems[index] = { ...orderItems[index], quantityOrdered: val };
      setOrderData({ ...orderData, orderItems });
    }
  };

  const validateQuantityFulfilled = (newQty: number, index: number) => {
  const errorKey = `order-quantity-fulfilled-${index}`;
  const item = orderData.orderItems[index];

  const isActiveItem = item.id ? item.isActive : true;
  const isValidContext = isActiveItem && item.quantityOrdered != null;

  setErrors((prevErrors) => {
    const updatedErrors = { ...prevErrors };

    if (isValidContext && newQty > item.quantityOrdered!) {
      updatedErrors[errorKey] = "Can't be greater than Quantity.";
    } else {
      delete updatedErrors[errorKey];
    }

    return updatedErrors;
  });
};
  const handleUpdateQuantityFulfilled = (newQty: number, index: number) => {
    setShowNotification(false);
    const orderItems = [...(orderData?.orderItems ?? [])];
    if (orderItems[index].quantityFulfilled !== newQty) {
      setFormStateChanged(true);
    }
    // Allow only integers
    if (/^-?\d*$/.test(newQty.toString())) {
      orderItems[index] = { ...orderItems[index], quantityFulfilled: newQty };
      setOrderData({ ...orderData, orderItems });
    }
  };

  function validateForm(name: string = "", value: string = "") {
    setErrors({});
    const newErrors: Record<string, string> = {};
    if (orderData?.orderItems) {
      orderData.orderItems.forEach((item, index) => {
        if (((item.id && item.isActive) || !item.id) && !item.itemName) {
          newErrors['order-itemname-' + index] = "Item name is required.";
        }
        if (((item.id && item.isActive) || !item.id) && (!item.quantityOrdered || (item.quantityOrdered && item.quantityOrdered < 1))) {
          newErrors['order-quantity-' + index] = "Quantity is required.";
        }
        if (((item.id && item.isActive) || !item.id) &&
          (orderStatus === ORDER_STATUS_COMPLETE ||
            orderStatus === ORDER_STATUS_PARTIALLYCOMPLETE) &&
          (item.quantityFulfilled === undefined ||
            item.quantityFulfilled === null ||
            isNaN(Number(item.quantityFulfilled)) ||
            item.quantityFulfilled < 0)
        ) {
          newErrors["order-quantity-fulfilled-" + index] =
            "Quantity fulfilled is required.";
        } if (((item.id && item.isActive) || !item.id) &&
          item.quantityFulfilled !== undefined &&
          item.quantityOrdered !== undefined &&
          item.quantityFulfilled > item.quantityOrdered
        ) {
          newErrors["order-quantity-fulfilled-" + index] =
            "Can't be greater than Quantity.";
        }
      });
    }

    if (
      !orderData?.orderItems.some(
        (item) => (item.id && item.isActive) || !item.id
      )
    ) {
      newErrors["order-item"] =
        "Please add at least one item to this order before submit.";
    }

    // Check for duplicate item names
    const itemNames = orderData?.orderItems
      .filter(item => (item.id && item.isActive) || !item.id)
      .map(item => item.itemName?.trim().toLowerCase())
      .filter(name => !!name); // remove empty/null names

    const hasDuplicateNames = new Set(itemNames).size !== itemNames.length;

    if (hasDuplicateNames) {
      newErrors["order-item"] =
        "Item names must be unique.";
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if there are no errors
  }

  const handleSaveOrder = async () => {
    if (!validateForm("save")) {
      return;
    }

    setIsLoading(true);

    // Deep clone and sanitize orderItems
    const updatedOrderData = {
      ...orderData,
      orderItems: orderData?.orderItems?.map(item => ({
        ...item,
        quantityFulfilled: item.quantityFulfilled ?? undefined
      })) ?? []
    };

    updatedOrderData.status = orderStatus;

    const result = await OrderService.update(updatedOrderData);

    if (result.success) {
      const updatedOrder = await OrderService.getByOrderNumber(orderData.orderNumber);
      setOrderData(updatedOrder.data);
      setOrderPdf({ ...orderPdf, data: updatedOrderData });
      setIsLoading(false);
      setShowNotification(true);
      setNotificationMessage({
        messageType: "success",
        messageHeader: "Order status updated.",
      });
      setFormStateChanged(false);
    } else {
      setIsLoading(false);
      setShowNotification(true);
      setNotificationMessage({
        messageType: "error",
        messageHeader: "Error - Updating Order status.",
      });
    }

    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const handleCancelOrder = async () => {
    setErrors({});
    setShowCancelOrderWarning(false);
    setReasonForCancellation("");
    setIsLoading(true);

    let updatedSuccess = false;

    // Create cancellation comment
    const cancellationComment: UserComment = {
      id: undefined,
      text: `Order cancelled (Reason: ${reasonForCancellation})`,
      username: auth.user?.profile.name ?? "",
      postDate: new Date().toISOString(),
      postDateString: `${dayjs().tz("America/Edmonton", false).format("dddd, MMMM D, YYYY [at] HH:mm")} (MST)`,
    };

    const updatedOrderData = { ...orderData };
    updatedOrderData.comments = [...(updatedOrderData.comments || []), cancellationComment]?.sort((a: UserComment, b: UserComment) => new Date(b.postDate).getTime() - new Date(a.postDate).getTime());
    updatedOrderData.status = ORDER_STATUS_CANCELLED;

    setOrderData(updatedOrderData);

    try {
      const updateResult = await OrderService.update(updatedOrderData);

      if (updateResult.success) {
        const commentResult = await OrderService.addComment(updatedOrderData);

        if (commentResult.success) {
          updatedSuccess = true;

          const pdfData = {
            ...updatedOrderData,
            statusDesc:
              orderActionStatusList.find((i) => i.value === updatedOrderData.status)?.label ?? "",
          };

          setOrderPdf({ ...orderPdf, data: pdfData });

          setNotificationMessage({
            messageType: "success",
            messageHeader: "Order successfully cancelled.",
          });
        }
      }
    } catch (error) {
      console.error("Cancellation error:", error);
    }

    if (!updatedSuccess) {
      setNotificationMessage({
        messageType: "error",
        messageHeader: "Error cancelling order.",
      });
    }

    setIsLoading(false);
    setShowNotification(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };


  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNotification(false);
    }, 30000);

    // Cleanup timer on unmount
    return () => clearTimeout(timer);
  }, [showNotification, setShowNotification]);

  const handleAddOrderItem = useCallback((orderType: string) => {
    const orderItems = [...(orderData?.orderItems ?? [])];
    orderItems.push(new OrderItem({ orderId: orderData?.id, itemType: orderType, isCrewTypeItem: orderType === "CrewType" }));
    setOrderData({ ...orderData, orderItems });

    setFocusIndex(orderItems.length - 1);
  }, [orderData, setOrderData]);

  const handleRemoveOrderItem = (id: string | undefined, index: number) => {

    const orderItems = [...(orderData?.orderItems ?? [])];
    if (!id) {
      orderItems?.splice(index, 1);
    } else {
      orderItems[index] = { ...orderItems[index], isActive: null };
    }
    setOrderData({ ...orderData, orderItems });
    setFormStateChanged(true);
    if (orderItems.length > 0) {
      setFocusIndex(orderItems.length - 1);
    }
  };


  // const isMyOrder = orderData.auditTrail?.createdBy === getUserEmail(auth?.user?.access_token ?? "");
  const isEditMode =
    orderData.status !== ORDER_STATUS_CANCELLED &&
    orderData.status !== ORDER_STATUS_COMPLETE;

  return (
    <>
      <PageLoader visible={loading} />
      <div className="ordered-items-container-new">
        {(orderData.orderType === "Personnel" || orderData.orderItems.some(x => x.isCrewTypeItem)) && <> <table className={"order-add-items-container"}><thead>
          <tr>
            <th className="col1"><Typography color="textPrimary" variant="h5">Crews</Typography>
            </th>
          </tr>
          <tr>
            <th className="col1">Items</th>
            <th className="col2">Quantity</th>
            <th className="order-item-th-processor">Quantity fullfilled</th>
          </tr>
        </thead>
          <tbody>
            <tr>
              <td>
                <GoAFormItem error={errors["order-item"]}></GoAFormItem>
              </td>
            </tr>

            {orderData.orderItems?.map((item, index) => {
              return (
                item.isCrewTypeItem && (!item.id || item.isActive) && (
                  <tr key={index}>
                    {isEditMode ? (
                      <td>
                        <GoAFormItem error={errors["order-itemname-crewtype-" + index]}>
                          <CrewTypePicker
                            ref={(el) => (inputRefs.current[index] = el)}
                            item={item}
                            selectedItems={orderData.orderItems}
                            index={index}
                            handleItemNameChange={(value: string, index: number) => handleItemNameChange("CrewType", value, index)}
                          />
                        </GoAFormItem>{" "}
                      </td>
                    ) : (
                      <td className="order-item-td-readonly">
                        <span>{item.itemName}</span>
                      </td>
                    )}

                    {isEditMode ? (
                      <td>
                        <GoAFormItem error={errors["order-quantity-" + index]}>
                          <NumericInput
                            width={isMobile ? 75 : 80}
                            handleQuantityChange={handleQuantityChange}
                            value={item.quantityOrdered || 0}
                            index={index}
                            verticalAlign="initial"
                          />
                        </GoAFormItem>{" "}
                      </td>
                    ) : (
                      <td className="order-item-td-readonly">
                        <span>{item.quantityOrdered || 0}</span>
                      </td>
                    )}

                    <td
                      className={`order-item-td-processor ${isEditMode ? "edit" : ""
                        }`}
                    >
                      {orderData.status !== ORDER_STATUS_CANCELLED &&
                        orderData.status !== ORDER_STATUS_COMPLETE ? (
                        <GoAFormItem error={errors["order-quantity-fulfilled-" + index]}>
                          <TextField
                            //className="order-item-quantity"
                            variant="outlined"
                            value={item.quantityFulfilled}

                            onChange={(e) => 
                              handleUpdateQuantityFulfilled(Number(e.target.value), index)
                            }

                            onBlur={(e) => 
                              validateQuantityFulfilled(Number(e.target.value), index)
                            }
                            fullWidth
                            inputProps={{
                              inputMode: "numeric",
                              pattern: "-?\\d*",
                            }}
                            sx={{
                              height: 40,
                              width: 80,
                              verticalAlign: "initial",
                              "& .MuiInputBase-root": { height: "100%" },
                            }}
                          />
                        </GoAFormItem>
                      ) : (
                        item.quantityFulfilled
                      )}
                    </td>


                    {isEditMode && (
                      <td className="remove-item-button">
                        <GoAIconButton
                          icon="trash"
                          variant="destructive"
                          onClick={() => {
                            setShowNotification(false);
                            handleRemoveOrderItem(item.id, index);
                          }}
                        ></GoAIconButton>
                      </td>
                    )}
                  </tr>
                )
              );
            })}
          </tbody>
        </table>
          {isEditMode && (
            <div>
              <GoAButton
                type="secondary"
                onClick={() => {
                  setShowNotification(false);
                  handleAddOrderItem('CrewType');
                }}
              >
                Add item +
              </GoAButton>
            </div>
          )}</>}

        {orderData.orderItems?.length > 0 && (
          <table className={"order-add-items-container"}>
            <thead>
              <tr>
                <th className="col1">{orderData.orderType === "Personnel" && <Typography color="textPrimary" variant="h5">Individual positions</Typography>}
                </th>
              </tr>
              <tr>
                <th className="col1">{`${orderData.orderType}`}</th>
                <th className="order-item-th-processor">Quantity</th>
                <th className="order-item-th-processor">Quantity fullfilled</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <GoAFormItem error={errors["order-item"]}></GoAFormItem>
                </td>
              </tr>
              {orderData.orderItems.map((item, index) => {
                return (

                  ((item.id && item.isActive && (!item.itemType || !item.isCrewTypeItem)) || (!item.id && !item.isCrewTypeItem)) && (
                    <tr style={{ height: "80px" }} key={index}>
                      <td
                        className={`order-item-td-processor ${isEditMode ? "edit" : ""
                          }`}
                      >
                        {isEditMode ? (
                          <GoAFormItem
                            error={errors["order-itemname-" + index]}
                          >
                            <DomainItemAutocomplete
                              ref={(el) => (inputRefs.current[index] = el)}
                              item={item}
                              selectedItems={orderData.orderItems}
                              lookupTypeId={orderTypeId}
                              index={index}
                              handleItemNameChange={(value: string, index: number) => handleItemNameChange("itemName", value, index)}
                            />
                          </GoAFormItem>
                        ) : (
                          item.itemName
                        )}
                      </td>
                      <td
                        className={`order-item-td-processor ${isEditMode ? "edit" : ""
                          }`}
                      >
                        {isEditMode ? (
                          <GoAFormItem
                            error={errors["order-quantity-" + index]}
                          >
                            <NumericInput
                              width={isMobile ? 75 : 80}
                              handleQuantityChange={handleQuantityChange}
                              value={item.quantityOrdered || 0}
                              index={index}
                              verticalAlign="initial"
                            />
                          </GoAFormItem>
                        ) : (
                          item.quantityOrdered
                        )}
                      </td>
                      <td
                        className={`order-item-td-processor ${isEditMode ? "edit" : ""
                          }`}
                      >
                        {orderData.status !== ORDER_STATUS_CANCELLED &&
                          orderData.status !== ORDER_STATUS_COMPLETE ? (
                          <GoAFormItem error={errors["order-quantity-fulfilled-" + index]}>
                            <TextField
                              //className="order-item-quantity"
                              variant="outlined"
                              value={item.quantityFulfilled}
                              onChange={(e) =>
                                handleUpdateQuantityFulfilled(
                                  Number(e.target.value),
                                  index
                                )
                              }

                              onBlur={(e) => validateQuantityFulfilled(Number(e.target.value), index)}
                              fullWidth
                              inputProps={{
                                inputMode: "numeric", // Ensures numeric keyboard on mobile
                                pattern: "-?\\d*", // Pattern for browser validation
                              }}
                              sx={{
                                height: 40,
                                width: 80,
                                verticalAlign: "initial",
                                "& .MuiInputBase-root": { height: "100%" },
                              }}
                            /></GoAFormItem>
                        ) : (
                          item.quantityFulfilled
                        )}
                      </td>
                      {isEditMode && (
                        <td
                          className={`remove-item-button order-item-td-processor ${isEditMode ? "edit" : ""
                            }`}
                        >
                          <GoAIconButton
                            icon="trash"
                            variant="destructive"
                            onClick={() => {
                              setShowNotification(false);
                              handleRemoveOrderItem(item.id, index);
                            }}
                          ></GoAIconButton>
                        </td>
                      )}
                    </tr>
                  )
                );
              })}
            </tbody>
          </table>
        )}
        {isEditMode && (
          <div>
            <GoAButton
              type="secondary"
              onClick={() => {
                setShowNotification(false);
                handleAddOrderItem(orderData.orderType);
              }}
            >
              Add item +
            </GoAButton>
          </div>
        )}
        <div className="comments-container">
          <OrderComments />
        </div>
        {orderData.status !== ORDER_STATUS_CANCELLED &&
          orderData.status !== ORDER_STATUS_COMPLETE && (
            <div style={{ marginTop: "50px" }}>
              <b>Order status</b>
            </div>
          )}
        {orderData.status !== ORDER_STATUS_CANCELLED &&
          orderData.status !== ORDER_STATUS_COMPLETE && (
            <div>
              <GoADropdown
                value={orderData.status.toString()}
                onChange={(name, value) => handleUpdateStatus(value as string)}
                placeholder="Select"
              >
                <GoADropdownItem
                  value="10"
                  label="Processing"
                ></GoADropdownItem>
                <GoADropdownItem value="8" label="Complete"></GoADropdownItem>
                <GoADropdownItem
                  value="13"
                  label="Unable to Complete"
                ></GoADropdownItem>
                <GoADropdownItem
                  value="14"
                  label="Partially Complete"
                ></GoADropdownItem>
              </GoADropdown>
            </div>
          )}

        <div
          className={`${isMobile
            ? "update-order-button-group-processor-mobile"
            : "update-order-button-group-processor"
            }`}
        >
          {orderData.status !== ORDER_STATUS_CANCELLED &&
            orderData.status !== ORDER_STATUS_COMPLETE && (
              <div className="update-order-button-1">
                <GoAButton
                  type="primary"
                  onClick={handleSaveOrder}
                  disabled={!formStateChanged}
                >
                  Save
                </GoAButton>
              </div>
            )}
          <div
            className={`update-order-button-2 ${orderData.status === ORDER_STATUS_CANCELLED ||
              orderData.status === ORDER_STATUS_COMPLETE
              ? "cancelled-state"
              : ""
              }`}
          >
            <GoAButton
              type="secondary"
              onClick={() =>
                formStateChanged
                  ? setShowDiscardChangesWarning(true)
                  : navigate("/ordersList")
              }
            >
              Back to list view
            </GoAButton>
          </div>
          {orderData.status !== ORDER_STATUS_CANCELLED &&
            orderData.status !== ORDER_STATUS_COMPLETE && (
              <div className="update-order-button-3">
                <GoAButton
                  // disabled={props.orderData.status === ORDER_STATUS_PARTIALLYCOMPLETE || props.orderData.status === ORDER_STATUS_COMPLETE}
                  type="primary"
                  variant="destructive"
                  onClick={() => setShowCancelOrderWarning(true)}
                >
                  Cancel order
                </GoAButton>
              </div>
            )}
        </div>
        {showNotification && (
          <Notification
            messageType={notificationMessage.messageType}
            messageHeader={notificationMessage.messageHeader}
          ></Notification>
        )}
      </div>
      <GoAModal heading="Cancel Order" open={showCancelOrderWarning}>
        <GoAFormItem label="Reason">
          <GoATextArea
            name="ReasonForCancellation"
            ariaLabel="Reason For Cancellation"
            countBy="character"
            maxCount={500}
            onChange={(name, value) => { setReasonForCancellation(value) }}
            value={reasonForCancellation}
          ></GoATextArea>
        </GoAFormItem>
        <div className="cancel-order-warning-button-group">
          <GoAButtonGroup alignment="end">
            <GoAButton
              type="secondary"
              onClick={() => { setReasonForCancellation(""); setShowCancelOrderWarning(false); }}
            >
              Close
            </GoAButton>
            <GoAButton type="primary" variant="destructive" onClick={handleCancelOrder} disabled={!reasonForCancellation || reasonForCancellation.length > 500}>
              Cancel Order
            </GoAButton>
          </GoAButtonGroup>
        </div>
      </GoAModal>
      <GoAModal
        heading="Are you sure you want to discard the changes?"
        open={showDiscardChangesWarning}
      >
        <GoAButtonGroup alignment="end">
          <GoAButton
            type="secondary"
            onClick={() => setShowDiscardChangesWarning(false)}
          >
            Cancel
          </GoAButton>
          <GoAButton type="primary" onClick={() => navigate("/ordersList")}>
            Confirm
          </GoAButton>
        </GoAButtonGroup>
      </GoAModal>
    </>
  );
};

export default OrderViewUpdateByprocessor;
